import { createFileRoute, Link } from "@tanstack/react-router";
import { getCounter } from "@/lib/warRoom/eventCounters";
import { listInfluencersByCountry } from "@/lib/growth/influencerRegistry";
import { getStateTeams } from "@/lib/growth/stateTeams";

/**
 * Brasil Growth Dashboard — reads from the existing per-device War Room
 * counters (already populated by trackEvent). NO fake metrics: shows real
 * device-local counts; backend aggregation happens in the existing War Room.
 */
export const Route = createFileRoute("/admin/brasil-growth")({
  head: () => ({
    meta: [
      { title: "Brasil Growth — VerseSport" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BrasilGrowthDashboard,
});

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-black leading-tight">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function BrasilGrowthDashboard() {
  const influencers = listInfluencersByCountry("BR");
  const states = getStateTeams("BR");

  const kpis = {
    influencerView: getCounter("influencer_page_view"),
    influencerSignup: getCounter("influencer_signup"),
    influencerReferral: getCounter("influencer_referral"),
    influencerShare: getCounter("influencer_share"),
    stateSelected: getCounter("state_team_selected"),
    whatsapp: getCounter("whatsapp_growth_cta_clicked"),
    telegram: getCounter("telegram_channel_cta_clicked"),
    prediction: getCounter("prediction_vote"),
    shares: getCounter("viral_share_completed"),
    referrals: getCounter("referral_signup"),
    d1: getCounter("day2_return_panel_shown"),
  };

  return (
    <main className="mx-auto max-w-5xl space-y-4 px-3 py-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-black">🇧🇷 Brasil Growth Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          KPIs ao vivo do canal Brasil. Métricas locais (este dispositivo) —
          agregação global vive no <Link to="/admin/world-cup-warroom" className="underline">War Room</Link>.
        </p>
      </header>

      <section className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi label="Influencer views" value={kpis.influencerView} hint="hoje" />
        <Kpi label="Signups" value={kpis.influencerSignup} hint="hoje" />
        <Kpi label="Referrals" value={kpis.referrals} hint="hoje" />
        <Kpi label="Shares" value={kpis.shares} hint="hoje" />
        <Kpi label="Predições" value={kpis.prediction} hint="hoje" />
        <Kpi label="WhatsApp CTA" value={kpis.whatsapp} hint="hoje" />
        <Kpi label="Telegram CTA" value={kpis.telegram} hint="hoje" />
        <Kpi label="D1 retorno" value={kpis.d1} hint="painel Dia 2 mostrado" />
      </section>

      <section className="rounded-2xl border border-border bg-background/60 p-3">
        <h2 className="mb-2 text-sm font-black">Influencers ativos (BR)</h2>
        <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:grid-cols-3">
          {influencers.map((i) => (
            <li key={i.slug}>
              <Link
                to="/brasil/influencer/$slug"
                params={{ slug: i.slug }}
                className="flex items-center justify-between rounded-lg border border-border px-2 py-1.5 text-[12px] font-bold hover:bg-accent"
              >
                <span><span aria-hidden>{i.emoji}</span> {i.displayName}</span>
                <span className="text-[10px] text-muted-foreground">/{i.slug}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-2xl border border-border bg-background/60 p-3">
        <h2 className="mb-2 text-sm font-black">Torcidas por Estado</h2>
        <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {states.map((s) => (
            <li key={s.id} className="rounded-lg border border-border bg-background/40 px-2 py-1.5 text-[12px] font-bold">
              <span aria-hidden>{s.emoji}</span> {s.name}
              <span className="float-right text-[10px] text-muted-foreground">{kpis.stateSelected ? "—" : "—"}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[10px] text-muted-foreground">
          Contadores globais serão agregados no War Room na próxima fase.
        </p>
      </section>
    </main>
  );
}